// The nine prompts on the Smart Tools board. Tapping one opens AI Chat with its opener ready to send.
export const POPULAR_PROMPTS = [
  {
    id: "pocket_editor",
    follows: ["Make it warmer", "Make it shorter"],
    ask: "Please enter your text.",
    label: "Pocket Editor",
    blurb: "Produce unique rewrites",
    tile: "/tiles/prompts/editor.jpg",
    starter:
      "Be my pocket editor. Rewrite the text below three different ways — clearer, warmer and shorter — and tell me what you changed.\n\n",
  },
  {
    id: "ghostwriter",
    follows: ["Keep my voice, polish more", "Give me a different angle"],
    ask: "Please enter the unfinished text.",
    label: "Ghostwriter",
    blurb: "Turn your unfinished draft into a polished piece",
    tile: "/tiles/prompts/ghostwriter.jpg",
    starter:
      "Be my ghostwriter. Turn my unfinished draft below into a polished piece, keeping my voice and asking me anything that is missing.\n\n",
  },
  {
    id: "essay_writer",
    follows: ["Add stronger evidence", "Make the conclusion punchier"],
    ask: "Give me the topic of your essay.",
    label: "Essay Writer",
    blurb: "Craft an essay with a clear structure",
    tile: "/tiles/prompts/essay.jpg",
    starter:
      "Write an essay with a clear structure — introduction, three arguments with evidence, and a conclusion. My topic is: ",
  },
  {
    id: "roleplay",
    follows: ["Stay in character, keep going", "Make it more playful"],
    ask: "Should I act as your friend, romantic partner, or someone else?",
    label: "Roleplay",
    blurb: "Engage with any AI persona",
    tile: "/tiles/prompts/roleplay.jpg",
    starter:
      "Let's roleplay. Stay fully in character and answer as they would, one reply at a time. The character I want you to be is: ",
  },
  {
    id: "songwriter",
    follows: ["Write another chorus", "Make it more emotional"],
    ask: "Could you provide details on the concept of your song, including who you would like to dedicate it to, and anyone you would like to mention in the lyrics? Please share as many specifics as you can.",
    label: "Songwriter",
    blurb: "Generate song lyrics on any subject matter",
    tile: "/tiles/prompts/songwriter.jpg",
    starter:
      "Write song lyrics with two verses, a chorus and a bridge. Name the mood and suggest a chord progression. The subject is: ",
  },
  {
    id: "engaging_posts",
    follows: ["Make the hook stronger", "Give me a shorter version"],
    ask: "What's the subject of your post?",
    label: "Engaging Posts",
    blurb: "Write posts that resonate with your audience",
    tile: "/tiles/prompts/posts.jpg",
    starter:
      "Write three social posts that resonate with my audience — one story-led, one useful tip, one bold opinion — each with a hook and hashtags. The topic is: ",
  },
  {
    id: "roast",
    follows: ["Go a bit harder", "Now say something nice"],
    ask: "What do you want to share on Instagram?",
    label: "Roast",
    blurb: "Get your feed roasted",
    tile: "/tiles/prompts/roast.jpg",
    starter:
      "Roast me. Be funny and sharp but never cruel, then finish with one genuinely useful tip. Here is what I am posting: ",
  },
  {
    id: "job_hunting",
    follows: ["Make the bullets stronger", "Write the cover letter now"],
    ask: "Could you describe your prior work experience, including the duration of each role?",
    label: "Job Hunting",
    blurb: "Craft a professional resume",
    tile: "/tiles/prompts/job.jpg",
    starter:
      "Help me job hunt. Turn my experience below into a professional resume with strong bullet points, then draft a short cover letter.\n\n",
  },
  {
    id: "business_hub",
    follows: ["Pick the best one and plan it", "Show me cheaper ideas"],
    ask: "Could you indicate the area or field you want to develop in?",
    label: "Business Hub",
    blurb: "Receive cost-free business ideas",
    tile: "/tiles/prompts/business.jpg",
    starter:
      "Give me five business ideas I can start with no money, based on my skills and where I live. For each one: who pays, first three steps, and what could go wrong. My skills are: ",
  },
];

// Every category in the "see all" drawer, with the colour its icons wear.
export const PROMPT_CATEGORIES = [
  { id: "writing", label: "Writing", tint: "text-brand" },
  { id: "social", label: "Social & content", tint: "text-pink-400" },
  { id: "work", label: "Work & career", tint: "text-amber-400" },
  { id: "money", label: "Business & money", tint: "text-forest" },
  { id: "study", label: "Study & learning", tint: "text-sky-400" },
  { id: "life", label: "Life & home", tint: "text-emerald-400" },
  { id: "fun", label: "Fun & play", tint: "text-orange-400" },
  { id: "language", label: "Language & travel", tint: "text-violet-400" },
];

const FEATURED_CATEGORY = {
  pocket_editor: "writing",
  ghostwriter: "writing",
  essay_writer: "study",
  roleplay: "fun",
  songwriter: "fun",
  engaging_posts: "social",
  roast: "fun",
  job_hunting: "work",
  business_hub: "money",
};

// The full library. Featured prompts appear here too so the drawer shows everything.
export const PROMPT_LIBRARY = [
  ...POPULAR_PROMPTS.map((p) => ({ ...p, category: FEATURED_CATEGORY[p.id], icon: "Sparkles" })),

  { id: "proofread", label: "Proofreader", category: "writing", icon: "SpellCheck",
    blurb: "Fix spelling and grammar without changing my voice",
    starter: "Proofread the text below. Fix spelling, grammar and punctuation only, keep my voice, and list what you changed.\n\n" },
  { id: "shorter", label: "Make It Shorter", category: "writing", icon: "Scissors",
    blurb: "Cut the words, keep the meaning",
    starter: "Cut the text below to half the length without losing anything important, then show the short version only.\n\n" },
  { id: "simpler", label: "Explain Simply", category: "writing", icon: "Baby",
    blurb: "Rewrite anything in plain words",
    starter: "Rewrite the text below in plain, simple words a 12-year-old would understand.\n\n" },
  { id: "story_idea", label: "Story Starter", category: "writing", icon: "BookOpen",
    blurb: "Three fresh openings for any idea",
    starter: "Give me three very different opening paragraphs for a story about: " },
  { id: "speech", label: "Speech Writer", category: "writing", icon: "Mic",
    blurb: "A short speech that lands",
    starter: "Write me a warm 2-minute speech with a strong opening line and a memorable ending. The occasion is: " },
  { id: "poem", label: "Poem", category: "writing", icon: "Feather",
    blurb: "A poem in the style you pick",
    starter: "Write a poem about the subject below. Tell me the style you chose and why it fits.\n\nSubject: " },

  { id: "captions", label: "Caption Machine", category: "social", icon: "Hash",
    blurb: "Ten captions with hashtags",
    starter: "Give me ten captions with hashtags for this post — mix funny, heartfelt and short. The post is about: " },
  { id: "video_script", label: "Short Video Script", category: "social", icon: "Video",
    blurb: "A 30-second script with a hook",
    starter: "Write a 30-second video script with a scroll-stopping hook, three beats and a call to action. The topic is: " },
  { id: "reply_comments", label: "Comment Replies", category: "social", icon: "MessageSquare",
    blurb: "Answer comments without the drama",
    starter: "Write friendly, confident replies to the comments below. Stay calm with the rude ones.\n\n" },
  { id: "bio", label: "Profile Bio", category: "social", icon: "UserRound",
    blurb: "A bio people remember",
    starter: "Write three short profile bios for me — one warm, one witty, one professional. Here is what I do: " },
  { id: "newsletter", label: "Newsletter", category: "social", icon: "Mail",
    blurb: "An email people actually open",
    starter: "Write a short newsletter with a subject line people open, one story, one useful tip and a clear ask. The topic is: " },
  { id: "content_plan", label: "Content Plan", category: "social", icon: "CalendarDays",
    blurb: "Two weeks of posts, planned",
    starter: "Plan two weeks of posts for me — one idea a day with the format and the hook. My audience and topic: " },

  { id: "cover_letter", label: "Cover Letter", category: "work", icon: "FileText",
    blurb: "A letter that fits the job",
    starter: "Write a one-page cover letter matching my experience to this job advert. Ask me for anything missing.\n\nJob advert: " },
  { id: "interview_prep", label: "Interview Prep", category: "work", icon: "MessagesSquare",
    blurb: "Practise the hard questions",
    starter: "Interview me for this role. Ask one question at a time, then score my answer and improve it. The role is: " },
  { id: "email_pro", label: "Work Email", category: "work", icon: "Send",
    blurb: "Say it clearly and politely",
    starter: "Write a clear, polite work email for the situation below. Keep it under 150 words.\n\nSituation: " },
  { id: "salary_talk", label: "Ask For A Raise", category: "work", icon: "TrendingUp",
    blurb: "Make the case for more pay",
    starter: "Help me ask for a raise. Build my case, script the conversation and prepare answers to three pushbacks. My situation: " },
  { id: "meeting_notes", label: "Meeting Notes", category: "work", icon: "ClipboardList",
    blurb: "Turn messy notes into actions",
    starter: "Turn the notes below into a clean summary with decisions, owners and next steps.\n\n" },
  { id: "linkedin", label: "LinkedIn Post", category: "work", icon: "Briefcase",
    blurb: "Sound human, not corporate",
    starter: "Write a LinkedIn post that sounds human, not corporate — a short story, one lesson, one question at the end. The topic is: " },

  { id: "business_plan", label: "One-Page Plan", category: "money", icon: "Target",
    blurb: "Your whole idea on one page",
    starter: "Turn my idea into a one-page plan: customer, offer, price, first ten customers and the biggest risk. My idea: " },
  { id: "pricing", label: "Pricing Helper", category: "money", icon: "Tag",
    blurb: "Price it without guessing",
    starter: "Help me price this. Suggest three tiers with what each includes and why someone upgrades. What I sell: " },
  { id: "budget", label: "Money Plan", category: "money", icon: "PiggyBank",
    blurb: "A budget that survives real life",
    starter: "Build me a simple monthly budget with a savings target and two painless cuts. My income and main costs: " },
  { id: "sales_pitch", label: "Sales Pitch", category: "money", icon: "Megaphone",
    blurb: "Sixty seconds that sells",
    starter: "Write a 60-second sales pitch: the problem, my answer, the proof and the ask. What I sell: " },
  { id: "market_check", label: "Rival Check", category: "money", icon: "Search",
    blurb: "See what you are up against",
    starter: "Compare me with three likely rivals: what they do better, where the gap is, and how I win. My business: " },
  { id: "invoice_chase", label: "Chase An Invoice", category: "money", icon: "ReceiptText",
    blurb: "Get paid, stay friendly",
    starter: "Write three reminder messages for a late invoice — gentle, firmer, final — staying professional. Details: " },

  { id: "study_plan", label: "Study Plan", category: "study", icon: "CalendarCheck",
    blurb: "A plan that fits the exam date",
    starter: "Build me a week-by-week study plan up to my exam, with what to revise and when to test myself. Subject and date: " },
  { id: "quiz_me", label: "Quiz Me", category: "study", icon: "ListChecks",
    blurb: "Ten questions, then the answers",
    starter: "Quiz me with ten questions on the topic below, one at a time, and mark each answer. Topic: " },
  { id: "explain_topic", label: "Teach Me This", category: "study", icon: "GraduationCap",
    blurb: "From zero to confident",
    starter: "Teach me this topic from scratch in five short steps with an example each, then check I understood. Topic: " },
  { id: "summarise", label: "Summarise This", category: "study", icon: "AlignLeft",
    blurb: "The key points in seconds",
    starter: "Summarise the text below in five bullet points and one sentence I could repeat to a friend.\n\n" },
  { id: "flashcards", label: "Flashcards", category: "study", icon: "Layers",
    blurb: "Twenty cards from any topic",
    starter: "Make 20 flashcards (question on one side, short answer on the other) for the topic: " },
  { id: "homework", label: "Homework Coach", category: "study", icon: "PencilLine",
    blurb: "Guides you, never just answers",
    starter: "Coach me through this homework — ask questions and give hints, do not give me the answer straight away.\n\n" },

  { id: "meal_plan", label: "Meal Plan", category: "life", icon: "UtensilsCrossed",
    blurb: "A week of meals and a shopping list",
    starter: "Plan a week of easy dinners with a shopping list. Tell me what to cook once and eat twice. My budget and tastes: " },
  { id: "declutter", label: "Declutter Plan", category: "life", icon: "Boxes",
    blurb: "One room at a time",
    starter: "Give me a 20-minute-a-day declutter plan for the room below, in the order that shows results fastest.\n\nRoom: " },
  { id: "fitness", label: "Move More", category: "life", icon: "HeartPulse",
    blurb: "A routine you will keep",
    starter: "Build me a realistic weekly exercise routine I can keep, with no gym and rising slowly. My level and time: " },
  { id: "gift_ideas", label: "Gift Ideas", category: "life", icon: "Gift",
    blurb: "Presents that actually land",
    starter: "Suggest ten gift ideas across three budgets for the person below, with why each one fits.\n\nAbout them: " },
  { id: "trip_plan", label: "Trip Planner", category: "life", icon: "Map",
    blurb: "A day-by-day itinerary",
    starter: "Plan my trip day by day with travel time, one must-see and one local favourite each day. Where, when and how long: " },
  { id: "hard_talk", label: "Difficult Talk", category: "life", icon: "HandHeart",
    blurb: "Say the hard thing kindly",
    starter: "Help me have a difficult conversation. Script my opening, likely replies and how to stay calm. The situation: " },

  { id: "would_you", label: "Party Questions", category: "fun", icon: "PartyPopper",
    blurb: "Games that get everyone talking",
    starter: "Give me 20 would-you-rather and never-have-I-ever questions for a group of friends, mostly light-hearted. The group is: " },
  { id: "riddles", label: "Riddles", category: "fun", icon: "Puzzle",
    blurb: "Puzzles with the answers hidden",
    starter: "Give me ten riddles from easy to hard, answers listed at the very end. Theme: " },
  { id: "dream", label: "Dream Reader", category: "fun", icon: "Moon",
    blurb: "A playful reading of your dream",
    starter: "Read my dream playfully — what it might mean, what stands out, and one nice thought to keep. My dream: " },
  { id: "pet_name", label: "Name Anything", category: "fun", icon: "Cat",
    blurb: "Names for pets, bands or babies",
    starter: "Give me 20 name ideas, grouped by style, with a line on each. What I am naming: " },
  { id: "debate", label: "Debate Me", category: "fun", icon: "Swords",
    blurb: "Argue the other side",
    starter: "Take the opposite side and debate me, one strong point at a time. Tell me when I win a point. My position: " },
  { id: "dm_help", label: "First Message", category: "fun", icon: "Heart",
    blurb: "Openers that get a reply",
    starter: "Write three opening messages that would get a reply — funny, curious and sincere. Here is their profile: " },

  { id: "say_it_like", label: "Say It Like A Local", category: "language", icon: "Languages",
    blurb: "The natural way, not the textbook",
    starter: "How would a local really say the phrase below? Give the natural version, the polite version and what to avoid.\n\nPhrase: " },
  { id: "grammar_why", label: "Why This Grammar", category: "language", icon: "HelpCircle",
    blurb: "The rule, in one clear answer",
    starter: "Explain the grammar in the sentence below simply, with two more examples and the mistake learners make.\n\nSentence: " },
  { id: "roleplay_shop", label: "Practise A Situation", category: "language", icon: "Store",
    blurb: "Rehearse before you go",
    starter: "Roleplay this situation with me line by line in the language I am learning, correcting me gently. The situation is: " },
  { id: "menu_help", label: "Read A Menu", category: "language", icon: "ChefHat",
    blurb: "Know what you are ordering",
    starter: "Explain this menu to me — what each dish is, what to avoid if I am cautious, and how to order politely.\n\nMenu: " },
  { id: "phone_call", label: "Make The Call", category: "language", icon: "Phone",
    blurb: "A script for the call you dread",
    starter: "Write me a phone script with the exact lines to say and likely answers, in both languages. The call is about: " },
  { id: "official_form", label: "Official Form", category: "language", icon: "FileCheck",
    blurb: "Understand what it asks for",
    starter: "Explain this official form line by line in plain words, and tell me exactly what to write. The form says:\n\n" },
];

export const PROMPT_STARTERS = Object.fromEntries(
  PROMPT_LIBRARY.map((p) => [p.id, p.starter])
);

export const DEFAULT_FOLLOWS = ["Make it shorter", "Give me another version"];

export const PROMPT_BY_ID = Object.fromEntries(PROMPT_LIBRARY.map((p) => [p.id, p]));

// The icons a user can pick for their own prompt (kept in step with CUSTOM_ICONS on the server).
export const ICON_CHOICES = [
  "Sparkles", "PenLine", "BookOpen", "Feather", "Mic", "Hash", "Video", "Mail", "Briefcase",
  "Target", "PiggyBank", "Megaphone", "GraduationCap", "ListChecks", "Layers", "UtensilsCrossed",
  "HeartPulse", "Gift", "Map", "PartyPopper", "Puzzle", "Moon", "Cat", "Swords", "Heart",
  "Languages", "Store", "ChefHat", "Phone", "FileCheck", "Wand2", "Rocket", "Lightbulb", "Star",
];
